'use client';

// Клиентский auth-state под cookie-модель (ARCH-D-028, AUTH-CONT-D-014).
// Источник истины: `contract.md` §7.3 (дословный листинг), `architecture.md` §2.13
// (ARCH-D-028 — что удаляется vs эталона `/srv/sintra/frontend/lib/auth/auth-store.tsx`).
//
// Ключевые отличия от эталона (ARCH-D-028):
//   • **Удалена** вся работа с `localStorage['sintra_access_token']` — токен живёт только
//     в httpOnly cookie; клиентский JS его не видит.
//   • **Удалён** `authenticatedRequest` (in-memory access + `Authorization`-заголовок) —
//     защищённые запросы идут через BFF route handlers; браузер прикрепляет cookie автоматически
//     (`credentials: 'same-origin'`).
//   • `AuthStatus` упрощён: `'loading' | 'authenticated' | 'unauthenticated'` (нет `'idle'` —
//     bootstrap синхронный по RSC-данным `(protected)/layout.tsx`, не требует клиентского таймера).
//   • `user`-state хранит `AuthUserSummary` (унифицированный user-shape, AUTH-CONT-D-003).
//   • `refreshSession()` → own-origin `POST /api/auth/refresh`; cookie ротированы сервером.
//     401 → `unauthenticated`. Dedup через `useRef<Promise | null>` (защита от storm'а
//     параллельных истёкших запросов).
//   • `login()`/`register()` → own-origin `POST /api/auth/{login,register}`; успех → store `user`,
//     redirect `/news` (cookie ставит route handler — клиентский JS токен не трогает).
//   • `logout()` → own-origin `POST /api/auth/logout`; всегда успех (idempotent, §5.4);
//     redirect `/login`.
//   • **Bootstrap серверный**: `(protected)/layout.tsx` зовёт `me` через `portalFetch` в RSC,
//     передаёт `user` в `<AuthProvider initialUser={...}>`. Client-store только хранит
//     полученное состояние; клиентских bootstrap-запросов при монтировании НЕ делает.

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useRouter } from 'next/navigation';
import {
  loginUser,
  registerUser,
  logoutUser,
  refreshSession as refreshRequest,
} from '@/lib/api/auth-client';
import type { RegisterPayload, LoginPayload } from '@/lib/api/schemas';

/**
 * Унифицированный user-shape (AUTH-CONT-D-003) — единый во всех 4 методах.
 *
 * `avatar_url` — опциональный: `me` отдаёт расширенный shape
 * (`profile.services.build_profile_user_payload`, контракт §2.0) с serve-путём
 * `/api/profile/avatar` при наличии фото; `register`/`login`/`refresh` используют
 * `auth.services.build_user_payload` (5 полей, без `avatar_url`). UI трактует
 * `avatar_url = null`/`undefined` как «фото нет → initials fallback».
 */
export interface AuthUserSummary {
  id: string;
  full_name: string | null;
  email: string;
  phone: string | null;
  registration_completed: boolean;
  /** Serve-путь `/api/profile/avatar` если фото есть; иначе null/отсутствует. */
  avatar_url?: string | null;
}

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthContextValue {
  user: AuthUserSummary | null;
  status: AuthStatus;
  initialized: boolean;
  isAuthenticated: boolean;
  login: (payload: LoginPayload) => Promise<void>;
  register: (payload: RegisterPayload) => Promise<void>;
  logout: () => Promise<void>;
  /** Успех → `true`; 401 (refresh невалиден/отсутствует) → `false`. Dedup через `useRef`. */
  refreshSession: () => Promise<boolean>;
  /** Seed пользователя из RSC (`(protected)/layout.tsx` уже сделал `me` серверно). */
  setUser: (user: AuthUserSummary | null) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({
  children,
  initialUser,
}: {
  children: ReactNode;
  initialUser: AuthUserSummary | null;
}) {
  const router = useRouter();
  const [user, setUserState] = useState<AuthUserSummary | null>(initialUser);
  // Bootstrap серверный: если layout передал initialUser → уже `authenticated`+`initialized`;
  // если не передал (public-page use) → `initialized=true` сразу, status='unauthenticated'
  // (проверка сессии делается серверно в RSC, не клиентским bootstrap-запросом — ARCH-D-028).
  const [initialized, setInitialized] = useState<boolean>(true);
  const refreshPromiseRef = useRef<Promise<boolean> | null>(null);

  const status: AuthStatus = user ? 'authenticated' : 'unauthenticated';
  const isAuthenticated = user !== null;

  const setUser = useCallback((next: AuthUserSummary | null) => {
    setUserState(next);
    setInitialized(true);
  }, []);

  const login = useCallback(
    async (payload: LoginPayload) => {
      // POST /api/auth/login (own-origin, credentials: 'same-origin'); cookie ставит route handler.
      // Успех → setUser(data.user); router.push('/news'). Ошибка → throw (форма покажет inline).
      const data = await loginUser(payload);
      setUserState(data.user);
      setInitialized(true);
      router.push('/news');
    },
    [router],
  );

  const register = useCallback(
    async (payload: RegisterPayload) => {
      // POST /api/auth/register (own-origin); cookie уже стоят (autologin, §8.1).
      // Успех → setUser(data.user); router.push('/news'). Ошибка → throw.
      const data = await registerUser(payload);
      setUserState(data.user);
      setInitialized(true);
      router.push('/news');
    },
    [router],
  );

  const logout = useCallback(async () => {
    // POST /api/auth/logout — идемпотентен (route handler всегда 200 + чистит cookie, §5.4).
    // Даже при сетевом сбое — setUser(null), redirect /login (не throw, §7.3).
    try {
      await logoutUser();
    } catch {
      // Сетевая ошибка / бэкенд недоступен — UX logout не должен падать; cookie всё равно
      // чистятся серверно при ближайшем успешном logout (или истекут по TTL). Клиентский
      // guard в `(protected)` всё равно перенаправит на /login на следующем заходе.
    }
    setUserState(null);
    setInitialized(true);
    router.push('/login');
  }, [router]);

  const refreshSession = useCallback(async (): Promise<boolean> => {
    // Dedup через useRef: два параллельных вызова (напр., две вкладки / два истёкших запроса)
    // ждут тот же Promise — иначе сервер-side atomic-UPDATE пропустит только один, а клиенту
    // незачем плодить запросы. Контракт §7.3 dedup-ref.
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    const promise = (async () => {
      try {
        const data = await refreshRequest();
        setUserState(data.user);
        setInitialized(true);
        return true;
      } catch {
        // Любая ошибка (вкл. UNAUTHENTICATED — refresh невалиден/отсутствует/отозван) → false.
        // Cookie чистит route handler §5.3 (clearAuthCookies на UNAUTHENTICATED/PERMISSION_DENIED).
        setUserState(null);
        setInitialized(true);
        return false;
      } finally {
        refreshPromiseRef.current = null;
      }
    })();

    refreshPromiseRef.current = promise;
    return promise;
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      status,
      initialized,
      isAuthenticated,
      login,
      register,
      logout,
      refreshSession,
      setUser,
    }),
    [user, status, initialized, isAuthenticated, login, register, logout, refreshSession, setUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
